import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import { ThreeColumnLayout } from '@dojo/widgets/three-column-layout';
import { TwoColumnLayout } from '@dojo/widgets/two-column-layout';
import { createNlsMiddleware, keymap, pathmap, userstate } from '@holmescorp/lcc-state';
import { TopicDetails, ParentTree } from '@holmescorp/lcc-state/interfaces';
import { Icon } from '@holmescorp/base-widgets';

import css from '../css/list-view.m.css';
import MicroCardButton from './card-button';
import { ContentContainer } from './container';
import { MicroProgress } from './progress';
export interface MicroListViewProperties {
	/* ** Unique key */
	key?: string;

	/* ** QA label */
	xqa?: string;

	/* ** Whether to use images for the list items */
	useImages?: boolean;

	/* ** The list of topics to be displayed */
	topics: TopicDetails[];

	/* ** Any action taken on a card that triggers a view switch */
	onAction?: (data?: { [key: string]: any }) => void;

	/* ** Additional classes to be applied to the list view */
	additionalClasses?: string[];
}

const factory = create({
	theme,
	userstate,
	ditabookNls: createNlsMiddleware('ditabook')
}).properties<MicroListViewProperties>();

export const MicroListView = factory(({ id, properties, middleware: { theme, userstate, ditabookNls } }) => {
	const { key, xqa, topics = [], onAction, useImages = true, additionalClasses = [] } = properties();
	const fixedCss = theme.classes(css);

	const {
		messages: {
			get_started = '[ditabook] get_started',
			card_button_label_middle: continue_label = '[ditabook] card_button_label_middle',
			card_button_label_finished: completed_label = '[ditabook] card_button_label_finished',
			assess_progress_threshold_high = '80'
		}
	} = ditabookNls();

	const overrides = {
		'@dojo/widgets/three-column-layout': {
			leading: [fixedCss.listLeadingOverride],
			center: [fixedCss.listCenterOverride],
			trailing: [fixedCss.listTrailingOverride]
		},
		'@dojo/widgets/two-column-layout': {
			column: [fixedCss.listTrailingOverride]
		}
	};

	const status: ParentTree = userstate.get(userstate.path('status'));
	const passingScore = parseInt(assess_progress_threshold_high) || 80;

	const listItems = topics.map((topic) => {
		const kids = topic.children?.filter((child) => !('shortdesc' in child)) || [];
		const isTopicGroup = kids.some((child) => 'topicgroup' in child);

		const key = topic.attributes?.key || '';
		const { path, title } = keymap.get(key)!;
		const { view } = pathmap.get(path)!;

		const { data: topicData } = status[key];
		const quizCompleted = isTopicGroup ? topicData.percent >= passingScore : true;
		const completed = quizCompleted && topicData.complete === topicData.total;
		const isactive = topicData.complete > 0 || topicData.percent > 0;

		const readTime = kids.reduce((acc, child) => {
			const attrKey = 'topicgroup' in child ? 'topicgroup' : 'topicref' in child ? 'topicref' : 'topicset';
			const time = child[attrKey].attributes?.readtime || 0;
			return acc + time;
		}, 0);

		const props = kids.reduce(
			(accum: { totalComplete: number }, child) => {
				const attrKey = 'topicgroup' in child ? 'topicgroup' : 'topicref' in child ? 'topicref' : 'topicset';
				const topicKey = child[attrKey].attributes?.rel || '';
				const { data } = status[topicKey];
				if (data.complete == data.total) {
					accum['totalComplete'] += 1;
				}
				return accum;
			},
			{ totalComplete: 0 }
		);

		const { totalComplete } = props;

		const leading = v(
			'div',
			{
				id: `icon-${key}`,
				role: 'img',
				...formatAriaProperties({ label: title }),
				classes: [fixedCss.listLeading]
			},
			[
				v('img', {
					alt: title,
					...formatAriaProperties({ labelledby: `icon-${key}`, hidden: 'true' }),
					title: title,
					src: `${window.hcstatic.portal_cdn}/js/images/${window.hcstatic.portal_site}/icons/icon_${key}.png`
				})
			]
		);

		const center = v('div', { classes: [fixedCss.listCenter] }, [
			v('div', { classes: [fixedCss.listItemTitle] }, [
				v('div', { classes: [fixedCss.listItemTitleText], xqa: 'progress-area-title' }, [title]),
				v('div', { classes: [fixedCss.listItemTitleText], xqa: 'estimated-read-time' }, [`${readTime} minutes`])
			]),
			v('div', { classes: [] }, [w(MicroProgress, { value: totalComplete, min: 0, max: kids.length })])
		]);

		const trailing = v('div', { classes: [fixedCss.actions] }, [
			w(MicroCardButton, {
				key: `card-button-${key}`,
				labelText: completed ? completed_label : isactive ? continue_label : get_started,
				darkMode: !completed,
				icon: completed ? w(Icon, { faname: 'rotate-left', size: 'sm' }) : null,
				onClick: (): void => {
					onAction && onAction();
					view();
				},
				onDown: (): void => {
					onAction && onAction();
					view();
				}
			})
		]);

		return v('div', { role: 'listitem', classes: [fixedCss.listItem] }, [
			useImages
				? w(ThreeColumnLayout, { bias: 'trailing', classes: overrides }, [
						{
							leading,
							center,
							trailing
						}
				  ])
				: w(TwoColumnLayout, { bias: 'leading', classes: overrides }, [{ leading: center, trailing }])
		]);
	});

	return w(ContentContainer, { id, key, xqa, additionalClasses }, [
		topics ? v('div', { role: 'list', classes: [theme.variant(), fixedCss.list] }, listItems) : null
	]);
});

export default MicroListView;
